import { useEffect } from 'react';

/** Class expected by the matching rule in `src/global.css`. */
const DISABLE_PTR_HTML_CLASS = 'checkout-disable-pull-to-refresh';

/**
 * ACDEV-29368 — publisher opt-in kill switch for browser pull-to-refresh
 * when the checkout owns the top-level browsing context.
 *
 * PTR at the top-level tab has two failure modes for a payment flow:
 *   1. A completed pull refreshes the page mid-payment — destructive.
 *   2. On iOS 17 Safari, an aborted mid-pull wedges the root scroll arbiter
 *      and freezes the inner scrollport until a forced layout probe clears
 *      it (reproduced against SB + STG on the same ticket).
 *
 * Adds the `checkout-disable-pull-to-refresh` class to `<html>` when BOTH
 * `enabled` (the publisher-scoped FF value) is true AND the checkout is the
 * top-level browsing context (`window.self === window.top`). The global CSS
 * scoped to that class in `src/global.css` locks the root element to
 * `height: 100dvh; overflow: hidden` — per the CSS Overflow spec the root's
 * `overflow` value propagates to the viewport, so the viewport itself
 * becomes non-scrollable and PTR cannot arm. Inner `overflow-y: auto`
 * scrollports inside `Modal` are unaffected because `Modal` is
 * `fixed inset-0` and CSS overflow does not cascade to descendants.
 *
 * Scope:
 *   - No-op inside an iframe (SDK / overlay embed): PTR belongs to the
 *     merchant page there and locking html scroll would have no effect on it.
 *   - No-op when the FF is OFF/absent: pre-flag behaviour byte-for-byte.
 *   - When both conditions are met, the class applies on every browser that
 *     loads the CSS. The rule is inert on desktop (no PTR to suppress);
 *     Android Chrome's PTR has the same failure mode as Safari's when it
 *     fires, so the fix is intentionally cross-platform at the CSS layer.
 */
export const useDisablePullToRefresh = (enabled: boolean): void => {
  useEffect(() => {
    if (!enabled || !isTopLevelFrame()) return;
    const html = document.documentElement;
    html.classList.add(DISABLE_PTR_HTML_CLASS);
    return () => {
      html.classList.remove(DISABLE_PTR_HTML_CLASS);
    };
  }, [enabled]);
};

/**
 * Whether the current script owns the top-level browsing context (checkout
 * loaded directly at the top-level domain) rather than running inside an
 * iframe. Cross-origin access to `window.top` can throw, and a throw itself
 * already proves we are framed — so the `catch` collapses to "definitely
 * NOT top-level". Same idiom as `isInIframe` in
 * `utils/applePayTabFallback.ts`.
 */
const isTopLevelFrame = (): boolean => {
  try {
    return window.self === window.top;
  } catch {
    return false;
  }
};
